import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import { axiosInstance } from "services/axios-supabase";

const API_URL = import.meta.env.VITE_API_URL;
const CACHE_TTL = 5 * 60 * 1000; // 5 minutes

const initialState = {
  user: null,
  preferences: null,
  company: null,
  notificationSettings: null,
  subscription: null,
  connections: [],
  calendars: [],
  clients: [],
  clientsLast7DaysTotals: {},
  watchers: [],
  invoices: [],
  logs: [],
  logsPagination: null,
  logsLoading: false,
  emails: [],
  emailsTotalCount: 0,
  emailsPage: 0,
  emailsPageSize: 25,
  emailsLoading: false,
  emailsError: null,
  insights: [],
  insightsLoading: false,
  availableInsights: [],
  runInsightLoading: false,
  runInsightError: null,
  deleteInsightLoading: false,
  calendarCache: [],
  calendarCacheLoading: false,
  calendarCacheError: null,
  rebuildCacheLoading: false,
  rebuildCacheError: null,
  deleteCacheLoading: false,
  calendarIntegrityResults: [],
  calendarIntegrityLoading: false,
  calendarIntegrityError: null,
  deleteUserCalendarLoading: false,
  loading: false,
  error: null,
  loaded: false,
  currentUserId: null,
  lastFetchTime: null,
};

// Fetch user detail
export const fetchUserDetail = createAsyncThunk(
  "userDetail/fetchUserDetail",
  async (userId, { getState }) => {
    const state = getState().userDetail;
    const now = Date.now();
    
    // Check cache
    if (
      state.currentUserId === userId &&
      state.lastFetchTime &&
      now - state.lastFetchTime < CACHE_TTL &&
      state.user
    ) {
      return { user: state.user, preferences: state.preferences };
    }

    const response = await axiosInstance.get(`${API_URL}/admin/users/${userId}`);
    return response.data;
  }
);

// Fetch user connections
export const fetchUserConnections = createAsyncThunk(
  "userDetail/fetchUserConnections",
  async (userId, { getState }) => {
    const state = getState().userDetail;
    const now = Date.now();
    
    if (
      state.currentUserId === userId &&
      state.lastFetchTime &&
      now - state.lastFetchTime < CACHE_TTL &&
      state.connections.length > 0
    ) {
      return state.connections;
    }

    const response = await axiosInstance.get(`${API_URL}/admin/users/${userId}/connections`);
    return response.data.connections || [];
  }
);

/** GET /admin/users/:userId/calendars/integrity for each distinct connection_id on loaded calendars. */
export const runCalendarIntegrityChecks = createAsyncThunk(
  "userDetail/runCalendarIntegrityChecks",
  async (userId, { getState, rejectWithValue }) => {
    const calendars = getState().userDetail.calendars || [];
    const connectionIds = [...new Set(calendars.map((c) => c.connection_id).filter(Boolean))];
    if (!connectionIds.length) {
      return [];
    }
    try {
      const results = await Promise.all(
        connectionIds.map(async (connection_id) => {
          const response = await axiosInstance.get(
            `${API_URL}/admin/users/${userId}/calendars/integrity`,
            { params: { connection_id } }
          );
          return response.data;
        })
      );
      return results;
    } catch (err) {
      return rejectWithValue(err.response?.data?.error || err.message || "Integrity check failed");
    }
  }
);

/** DELETE /admin/users/:userId/calendars/:calendarRowId — row `id` (UUID), not provider calendar_id. */
export const deleteUserCalendarRow = createAsyncThunk(
  "userDetail/deleteUserCalendarRow",
  async ({ userId, calendarRowId }, { rejectWithValue, dispatch }) => {
    try {
      await axiosInstance.delete(`${API_URL}/admin/users/${userId}/calendars/${calendarRowId}`);
      await dispatch(fetchUserCalendars(userId));
      return { calendarRowId };
    } catch (err) {
      return rejectWithValue(err.response?.data?.error || err.message || "Delete failed");
    }
  }
);

// Fetch user calendars
export const fetchUserCalendars = createAsyncThunk(
  "userDetail/fetchUserCalendars",
  async (userId, { getState }) => {
    const state = getState().userDetail;
    const now = Date.now();
    
    if (
      state.currentUserId === userId &&
      state.lastFetchTime &&
      now - state.lastFetchTime < CACHE_TTL &&
      state.calendars.length > 0
    ) {
      return state.calendars;
    }

    const response = await axiosInstance.get(`${API_URL}/admin/users/${userId}/calendars`);
    return response.data.calendars || [];
  }
);

// Fetch user clients
export const fetchUserClients = createAsyncThunk(
  "userDetail/fetchUserClients",
  async (userId, { getState }) => {
    const state = getState().userDetail;
    const now = Date.now();
    
    if (
      state.currentUserId === userId &&
      state.lastFetchTime &&
      now - state.lastFetchTime < CACHE_TTL &&
      state.clients.length > 0
    ) {
      return state.clients;
    }

    const response = await axiosInstance.get(`${API_URL}/admin/users/${userId}/clients`);
    return response.data.clients || [];
  }
);

// Fetch last 7 days totals for clients
export const fetchUserClientsLast7Days = createAsyncThunk(
  "userDetail/fetchUserClientsLast7Days",
  async (userId, { getState }) => {
    const response = await axiosInstance.get(`${API_URL}/admin/users/${userId}/clients/last7Days`);
    return response.data.totals || {};
  }
);

// Fetch user watchers
export const fetchUserWatchers = createAsyncThunk(
  "userDetail/fetchUserWatchers",
  async (userId, { getState }) => {
    const state = getState().userDetail;
    const now = Date.now();
    
    if (
      state.currentUserId === userId &&
      state.lastFetchTime &&
      now - state.lastFetchTime < CACHE_TTL &&
      state.watchers.length > 0
    ) {
      return state.watchers;
    }

    const response = await axiosInstance.get(`${API_URL}/admin/users/${userId}/watchers`);
    return response.data.watchers || [];
  }
);

// Fetch user invoices
export const fetchUserInvoices = createAsyncThunk(
  "userDetail/fetchUserInvoices",
  async (userId, { getState }) => {
    const state = getState().userDetail;
    const now = Date.now();
    
    if (
      state.currentUserId === userId &&
      state.lastFetchTime &&
      now - state.lastFetchTime < CACHE_TTL &&
      state.invoices.length > 0
    ) {
      return state.invoices;
    }

    const response = await axiosInstance.get(`${API_URL}/admin/users/${userId}/invoices`);
    return response.data.invoices || [];
  }
);

// Fetch user company
export const fetchUserCompany = createAsyncThunk(
  "userDetail/fetchUserCompany",
  async (userId, { getState }) => {
    const state = getState().userDetail;
    const now = Date.now();
    
    if (
      state.currentUserId === userId &&
      state.lastFetchTime &&
      now - state.lastFetchTime < CACHE_TTL &&
      state.company
    ) {
      return state.company;
    }

    const response = await axiosInstance.get(`${API_URL}/admin/users/${userId}/company`);
    return response.data.company || null;
  }
);

// Fetch user subscription (pass { userId, force: true } to bypass short TTL cache)
export const fetchUserSubscription = createAsyncThunk(
  "userDetail/fetchUserSubscription",
  async (arg, { getState }) => {
    const userId = typeof arg === "string" ? arg : arg?.userId;
    const force = typeof arg === "object" && arg?.force === true;
    if (!userId) {
      throw new Error("userId is required");
    }
    const state = getState().userDetail;
    const now = Date.now();

    if (
      !force &&
      state.currentUserId === userId &&
      state.lastFetchTime &&
      now - state.lastFetchTime < CACHE_TTL &&
      state.subscription !== undefined
    ) {
      return state.subscription;
    }

    const response = await axiosInstance.get(`${API_URL}/admin/users/${userId}/subscription`);
    return response.data.subscription || null;
  }
);

// Update user subscription
export const updateUserSubscription = createAsyncThunk(
  "userDetail/updateUserSubscription",
  async ({ userId, payload }, { rejectWithValue }) => {
    try {
      const response = await axiosInstance.patch(
        `${API_URL}/admin/users/${userId}/subscription`,
        payload
      );
      return { subscription: response.data.subscription };
    } catch (err) {
      return rejectWithValue(err.response?.data?.error || err.message || "Failed to save");
    }
  }
);

// Recompute activity_notifications_eligible for one user (same logic as nightly cron)
export const reconcileUserSubscriptionActivityEligibility = createAsyncThunk(
  "userDetail/reconcileUserSubscriptionActivityEligibility",
  async (userId, { rejectWithValue, dispatch }) => {
    try {
      const response = await axiosInstance.post(
        `${API_URL}/admin/users/${userId}/subscription/reconcile-activity-eligibility`
      );
      await dispatch(fetchUserSubscription({ userId, force: true }));
      return response.data;
    } catch (err) {
      return rejectWithValue(
        err.response?.data?.error || err.message || "Failed to reconcile activity eligibility"
      );
    }
  }
);

// Fetch user notification settings
export const fetchUserNotificationSettings = createAsyncThunk(
  "userDetail/fetchUserNotificationSettings",
  async (userId, { getState }) => {
    const state = getState().userDetail;
    const now = Date.now();
    
    if (
      state.currentUserId === userId &&
      state.lastFetchTime &&
      now - state.lastFetchTime < CACHE_TTL &&
      state.notificationSettings
    ) {
      return state.notificationSettings;
    }

    const response = await axiosInstance.get(`${API_URL}/admin/users/${userId}/notification-settings`);
    return response.data.notificationSettings || null;
  }
);

// Fetch user emails (Postmark outbound history)
export const fetchUserEmails = createAsyncThunk(
  "userDetail/fetchUserEmails",
  async ({ userId, page = 0, pageSize = 25 }, { rejectWithValue }) => {
    try {
      const offset = page * pageSize;
      const response = await axiosInstance.get(`${API_URL}/admin/users/${userId}/emails`, {
        params: { count: pageSize, offset },
      });
      return {
        messages: response.data.messages || [],
        totalCount: response.data.totalCount ?? 0,
        recipient: response.data.recipient,
        page,
        pageSize,
      };
    } catch (err) {
      return rejectWithValue(err.response?.data?.error || err.message || "Failed to load emails");
    }
  }
);

// Fetch user insights
export const fetchUserInsights = createAsyncThunk(
  "userDetail/fetchUserInsights",
  async (userId) => {
    const response = await axiosInstance.get(`${API_URL}/admin/users/${userId}/insights`);
    return {
      insights: response.data.insights || [],
      availableInsights: response.data.availableInsights || [],
    };
  }
);

// Delete insight run
export const deleteUserInsightRun = createAsyncThunk(
  "userDetail/deleteUserInsightRun",
  async ({ userId, runId }, { rejectWithValue }) => {
    try {
      await axiosInstance.delete(`${API_URL}/admin/users/${userId}/insights/${runId}`);
      return { runId };
    } catch (err) {
      return rejectWithValue(err.response?.data?.error || err.message || "Failed to delete insight run");
    }
  }
);

// Run insight for user
export const runUserInsight = createAsyncThunk(
  "userDetail/runUserInsight",
  async ({ userId, insightId, asOfDate }, { rejectWithValue }) => {
    try {
      const body = { insightId };
      if (asOfDate) body.asOfDate = asOfDate;
      const response = await axiosInstance.post(
        `${API_URL}/admin/users/${userId}/insights/run`,
        body
      );
      return response.data;
    } catch (err) {
      return rejectWithValue(err.response?.data?.error || err.message || "Failed to run insight");
    }
  }
);

// Fetch user calendar cache
export const fetchUserCalendarCache = createAsyncThunk(
  "userDetail/fetchUserCalendarCache",
  async (userId, { rejectWithValue }) => {
    try {
      const response = await axiosInstance.get(`${API_URL}/admin/users/${userId}/calendar-cache`);
      return {
        records: response.data.records || [],
        total: response.data.total ?? 0,
      };
    } catch (err) {
      return rejectWithValue(err.response?.data?.error || err.message || "Failed to load calendar cache");
    }
  }
);

// Rebuild user calendar cache
export const rebuildUserCalendarCache = createAsyncThunk(
  "userDetail/rebuildUserCalendarCache",
  async (userId, { rejectWithValue }) => {
    try {
      const response = await axiosInstance.post(`${API_URL}/admin/users/${userId}/calendar-cache/rebuild`);
      return response.data;
    } catch (err) {
      return rejectWithValue(err.response?.data?.error || err.message || "Failed to rebuild calendar cache");
    }
  }
);

// Delete calendar cache record
export const deleteUserCalendarCacheRecord = createAsyncThunk(
  "userDetail/deleteUserCalendarCacheRecord",
  async ({ userId, recordId }, { rejectWithValue }) => {
    try {
      await axiosInstance.delete(`${API_URL}/admin/users/${userId}/calendar-cache/${recordId}`);
      return { recordId };
    } catch (err) {
      return rejectWithValue(err.response?.data?.error || err.message || "Failed to delete cache record");
    }
  }
);

// Fetch user logs
export const fetchUserLogs = createAsyncThunk(
  "userDetail/fetchUserLogs",
  async ({ userId, page = 1, pageSize = 25 }, { getState }) => {
    const response = await axiosInstance.get(`${API_URL}/admin/users/${userId}/logs`, {
      params: { page, pageSize },
    });
    return {
      logs: response.data.logs || [],
      pagination: response.data.pagination || null,
    };
  }
);

// Update user preferences
export const updateUserPreferences = createAsyncThunk(
  "userDetail/updateUserPreferences",
  async ({ userId, payload }, { rejectWithValue }) => {
    try {
      const response = await axiosInstance.patch(
        `${API_URL}/admin/users/${userId}/preferences`,
        payload
      );
      return { preferences: response.data.preferences };
    } catch (err) {
      return rejectWithValue(err.response?.data?.error || err.message || "Failed to save");
    }
  }
);

// Update user company (invoice settings)
export const updateUserCompany = createAsyncThunk(
  "userDetail/updateUserCompany",
  async ({ userId, payload }, { rejectWithValue }) => {
    try {
      const response = await axiosInstance.patch(
        `${API_URL}/admin/users/${userId}/company`,
        payload
      );
      return { company: response.data.company };
    } catch (err) {
      return rejectWithValue(err.response?.data?.error || err.message || "Failed to save");
    }
  }
);

// Update user notification settings
export const updateUserNotificationSettings = createAsyncThunk(
  "userDetail/updateUserNotificationSettings",
  async ({ userId, payload }, { rejectWithValue }) => {
    try {
      const response = await axiosInstance.patch(
        `${API_URL}/admin/users/${userId}/notification-settings`,
        payload
      );
      return { notificationSettings: response.data.notificationSettings };
    } catch (err) {
      return rejectWithValue(err.response?.data?.error || err.message || "Failed to save");
    }
  }
);

// Fetch all user detail data
export const fetchAllUserDetail = createAsyncThunk(
  "userDetail/fetchAllUserDetail",
  async (userId, { dispatch }) => {
    await Promise.all([
      dispatch(fetchUserDetail(userId)),
      dispatch(fetchUserConnections(userId)),
      dispatch(fetchUserCalendars(userId)),
      dispatch(fetchUserClients(userId)),
      dispatch(fetchUserWatchers(userId)),
      dispatch(fetchUserInvoices(userId)),
      dispatch(fetchUserClientsLast7Days(userId)),
      dispatch(fetchUserCompany(userId)),
      dispatch(fetchUserNotificationSettings(userId)),
      dispatch(fetchUserSubscription(userId)),
    ]);
  }
);

const userDetailSlice = createSlice({
  name: "userDetail",
  initialState,
  reducers: {
    resetUserDetail: () => initialState,
    clearCalendarIntegrity: (state) => {
      state.calendarIntegrityResults = [];
      state.calendarIntegrityError = null;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchUserDetail.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchUserDetail.fulfilled, (state, action) => {
        state.loading = false;
        state.user = action.payload.user;
        state.preferences = action.payload.preferences;
        state.currentUserId = action.payload.user?.id;
        state.lastFetchTime = Date.now();
        state.loaded = true;
      })
      .addCase(fetchUserDetail.rejected, (state, action) => {
        state.loading = false;
        state.error = action.error.message;
      })
      .addCase(fetchUserConnections.fulfilled, (state, action) => {
        state.connections = action.payload;
        state.lastFetchTime = Date.now();
      })
      .addCase(fetchUserCalendars.fulfilled, (state, action) => {
        state.calendars = action.payload;
        state.lastFetchTime = Date.now();
      })
      .addCase(fetchUserClients.fulfilled, (state, action) => {
        state.clients = action.payload;
        state.lastFetchTime = Date.now();
      })
      .addCase(fetchUserClientsLast7Days.fulfilled, (state, action) => {
        state.clientsLast7DaysTotals = action.payload;
        state.lastFetchTime = Date.now();
      })
      .addCase(fetchUserWatchers.fulfilled, (state, action) => {
        state.watchers = action.payload;
        state.lastFetchTime = Date.now();
      })
      .addCase(fetchUserInvoices.fulfilled, (state, action) => {
        state.invoices = action.payload;
        state.lastFetchTime = Date.now();
      })
      .addCase(fetchUserCompany.fulfilled, (state, action) => {
        state.company = action.payload;
        state.lastFetchTime = Date.now();
      })
      .addCase(fetchUserNotificationSettings.fulfilled, (state, action) => {
        state.notificationSettings = action.payload;
        state.lastFetchTime = Date.now();
      })
      .addCase(fetchUserSubscription.fulfilled, (state, action) => {
        state.subscription = action.payload;
        state.lastFetchTime = Date.now();
      })
      .addCase(fetchUserEmails.pending, (state) => {
        state.emailsLoading = true;
      })
      .addCase(fetchUserEmails.fulfilled, (state, action) => {
        state.emailsLoading = false;
        state.emails = action.payload.messages;
        state.emailsTotalCount = action.payload.totalCount;
        state.emailsPage = action.payload.page ?? 0;
        state.emailsPageSize = action.payload.pageSize ?? 25;
        state.emailsError = null;
      })
      .addCase(fetchUserEmails.rejected, (state, action) => {
        state.emailsLoading = false;
        state.emails = [];
        state.emailsTotalCount = 0;
        state.emailsError = action.payload || action.error?.message || "Failed to load emails";
      })
      .addCase(fetchUserInsights.pending, (state) => {
        state.insightsLoading = true;
      })
      .addCase(fetchUserInsights.fulfilled, (state, action) => {
        state.insightsLoading = false;
        state.insights = action.payload.insights;
        state.availableInsights = action.payload.availableInsights || [];
      })
      .addCase(fetchUserInsights.rejected, (state) => {
        state.insightsLoading = false;
        state.insights = [];
      })
      .addCase(runUserInsight.pending, (state) => {
        state.runInsightLoading = true;
        state.runInsightError = null;
      })
      .addCase(runUserInsight.fulfilled, (state) => {
        state.runInsightLoading = false;
        state.runInsightError = null;
      })
      .addCase(runUserInsight.rejected, (state, action) => {
        state.runInsightLoading = false;
        state.runInsightError = action.payload || action.error?.message || "Failed to run insight";
      })
      .addCase(deleteUserInsightRun.pending, (state) => {
        state.deleteInsightLoading = true;
      })
      .addCase(deleteUserInsightRun.fulfilled, (state, action) => {
        state.deleteInsightLoading = false;
        state.insights = state.insights.filter((r) => r.id !== action.payload.runId);
      })
      .addCase(deleteUserInsightRun.rejected, (state) => {
        state.deleteInsightLoading = false;
      })
      .addCase(fetchUserCalendarCache.pending, (state) => {
        state.calendarCacheLoading = true;
        state.calendarCacheError = null;
      })
      .addCase(fetchUserCalendarCache.fulfilled, (state, action) => {
        state.calendarCacheLoading = false;
        state.calendarCache = action.payload.records;
        state.calendarCacheError = null;
      })
      .addCase(fetchUserCalendarCache.rejected, (state, action) => {
        state.calendarCacheLoading = false;
        state.calendarCache = [];
        state.calendarCacheError = action.payload || action.error?.message || "Failed to load calendar cache";
      })
      .addCase(rebuildUserCalendarCache.pending, (state) => {
        state.rebuildCacheLoading = true;
        state.rebuildCacheError = null;
      })
      .addCase(rebuildUserCalendarCache.fulfilled, (state) => {
        state.rebuildCacheLoading = false;
        state.rebuildCacheError = null;
      })
      .addCase(rebuildUserCalendarCache.rejected, (state, action) => {
        state.rebuildCacheLoading = false;
        state.rebuildCacheError = action.payload || action.error?.message || "Failed to rebuild calendar cache";
      })
      .addCase(deleteUserCalendarCacheRecord.pending, (state) => {
        state.deleteCacheLoading = true;
      })
      .addCase(deleteUserCalendarCacheRecord.fulfilled, (state, action) => {
        state.deleteCacheLoading = false;
        state.calendarCache = state.calendarCache.filter((r) => r.id !== action.payload.recordId);
      })
      .addCase(deleteUserCalendarCacheRecord.rejected, (state) => {
        state.deleteCacheLoading = false;
      })
      .addCase(runCalendarIntegrityChecks.pending, (state) => {
        state.calendarIntegrityLoading = true;
        state.calendarIntegrityError = null;
      })
      .addCase(runCalendarIntegrityChecks.fulfilled, (state, action) => {
        state.calendarIntegrityLoading = false;
        state.calendarIntegrityResults = action.payload;
        state.calendarIntegrityError = null;
      })
      .addCase(runCalendarIntegrityChecks.rejected, (state, action) => {
        state.calendarIntegrityLoading = false;
        state.calendarIntegrityResults = [];
        state.calendarIntegrityError = action.payload || action.error?.message || "Integrity check failed";
      })
      .addCase(deleteUserCalendarRow.pending, (state) => {
        state.deleteUserCalendarLoading = true;
      })
      .addCase(deleteUserCalendarRow.fulfilled, (state) => {
        state.deleteUserCalendarLoading = false;
        state.calendarIntegrityResults = [];
        state.calendarIntegrityError = null;
      })
      .addCase(deleteUserCalendarRow.rejected, (state) => {
        state.deleteUserCalendarLoading = false;
      })
      .addCase(fetchUserLogs.pending, (state) => {
        state.logsLoading = true;
      })
      .addCase(fetchUserLogs.fulfilled, (state, action) => {
        state.logsLoading = false;
        state.logs = action.payload.logs;
        state.logsPagination = action.payload.pagination;
      })
      .addCase(fetchUserLogs.rejected, (state, action) => {
        state.logsLoading = false;
        state.error = action.error.message;
      })
      .addCase(fetchAllUserDetail.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchAllUserDetail.fulfilled, (state) => {
        state.loading = false;
        state.loaded = true;
      })
      .addCase(fetchAllUserDetail.rejected, (state, action) => {
        state.loading = false;
        state.error = action.error.message;
      })
      .addCase(updateUserPreferences.fulfilled, (state, action) => {
        if (action.payload?.preferences) state.preferences = action.payload.preferences;
      })
      .addCase(updateUserCompany.fulfilled, (state, action) => {
        if (action.payload?.company) state.company = action.payload.company;
      })
      .addCase(updateUserNotificationSettings.fulfilled, (state, action) => {
        if (action.payload?.notificationSettings) state.notificationSettings = action.payload.notificationSettings;
      })
      .addCase(updateUserSubscription.fulfilled, (state, action) => {
        if (action.payload?.subscription) state.subscription = action.payload.subscription;
      });
  },
});

export const { resetUserDetail, clearCalendarIntegrity } = userDetailSlice.actions;
export default userDetailSlice.reducer;
